import { useTranslation } from "react-i18next";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import type { ChecklistItem } from "./artifactTables";
import WordingsInput from "./WordingsInput";

const EMPTY_ITEM: ChecklistItem = { id: "", requirement: "", reference: "", part_type: "", labels: [], xbrl_concept: "" };

type TextColumn = "id" | "requirement" | "reference" | "part_type" | "xbrl_concept";

const TEXT_COLUMNS: readonly TextColumn[] = ["id", "requirement", "reference", "part_type"];

/** `dmw-disclosure-checklist/1` as a table (AU-5): ID, requirement, reference, part type, labels, XBRL concept. */
export default function ChecklistTable({ rows, onChange }: { rows: readonly ChecklistItem[]; onChange: (rows: readonly ChecklistItem[]) => void }) {
  const { t } = useTranslation();
  const replace = (index: number, row: ChecklistItem | null) => {
    onChange(rows.flatMap((other, position) => (position !== index ? [other] : row === null ? [] : [row])));
  };
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <caption className="sr-only">{t("authoring.artifact.checklist.caption")}</caption>
          <thead>
            <tr className="app-text-muted text-left">
              {TEXT_COLUMNS.map((column) => <th key={column} className="px-1 py-1">{t(`authoring.artifact.checklist.columns.${column}`)}</th>)}
              <th className="px-1 py-1">{t("authoring.artifact.checklist.columns.labels")}</th>
              <th className="px-1 py-1">{t("authoring.artifact.checklist.columns.xbrl_concept")}</th>
              <th className="px-1 py-1"><span className="sr-only">{t("authoring.artifact.rowActions")}</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const name = row.id === "" ? String(index + 1) : row.id;
              return (
                <tr key={index} className="align-top">
                  {TEXT_COLUMNS.map((column) => (
                    <td key={column} className="px-1 py-0.5">
                      {column === "requirement" ? (
                        <textarea className={FORM_COMPACT_CONTROL_CLASS} rows={2} aria-label={t("authoring.artifact.checklist.cellOf", { column: t(`authoring.artifact.checklist.columns.${column}`), name })} value={row[column]} onChange={(event) => { replace(index, { ...row, [column]: event.target.value }); }} />
                      ) : (
                        <input className={`${FORM_COMPACT_CONTROL_CLASS} ${column === "id" ? "font-mono" : ""}`} aria-label={t("authoring.artifact.checklist.cellOf", { column: t(`authoring.artifact.checklist.columns.${column}`), name })} value={row[column]} onChange={(event) => { replace(index, { ...row, [column]: event.target.value }); }} />
                      )}
                    </td>
                  ))}
                  <td className="px-1 py-0.5">
                    <WordingsInput
                      className={`${FORM_COMPACT_CONTROL_CLASS} font-mono`}
                      label={t("authoring.artifact.checklist.cellOf", { column: t("authoring.artifact.checklist.columns.labels"), name })}
                      wordings={row.labels}
                      onChange={(labels) => { replace(index, { ...row, labels }); }}
                    />
                  </td>
                  <td className="px-1 py-0.5">
                    <input className={`${FORM_COMPACT_CONTROL_CLASS} font-mono`} aria-label={t("authoring.artifact.checklist.cellOf", { column: t("authoring.artifact.checklist.columns.xbrl_concept"), name })} value={row.xbrl_concept} onChange={(event) => { replace(index, { ...row, xbrl_concept: event.target.value }); }} />
                  </td>
                  <td className="px-1 py-0.5">
                    <AppButton variant="ghost" size="xs" aria-label={t("authoring.artifact.removeRowNamed", { name })} onClick={() => { replace(index, null); }}>
                      {t("authoring.artifact.removeRow")}
                    </AppButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <AppButton variant="secondary" size="xs" onClick={() => { onChange([...rows, EMPTY_ITEM]); }}>{t("authoring.artifact.checklist.addItem")}</AppButton>
      <p className="app-help-text text-xs">{t("authoring.artifact.checklist.hint")}</p>
    </div>
  );
}
